import { useMemo } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Users } from "lucide-react";

const ROLE_CONFIG = {
  admin:         { label: "Admin",         className: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300" },
  hr:            { label: "RH",            className: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300" },
  manager:       { label: "Manager",       className: "bg-[#E8F5E9] text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70]" },
  collaborateur: { label: "Collaborateur", className: "bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300" },
  employee:      { label: "Collaborateur", className: "bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300" },
  comptabilité:  { label: "Comptabilité",  className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
};

const ROLE_ORDER = { admin: 0, hr: 1, manager: 2, collaborateur: 3, employee: 3, comptabilité: 4 };

const getInitials = (first, last) =>
  `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase();

// ── Build tree ────────────────────────────────────────────────────────────────

const buildOrgTree = (users) => {
  const map = {};
  users.forEach((u) => { map[u.id] = { ...u, children: [] }; });
  const roots = [];
  users.forEach((u) => {
    if (u.manager_id && map[u.manager_id]) {
      map[u.manager_id].children.push(map[u.id]);
    } else {
      roots.push(map[u.id]);
    }
  });
  // Sort roots by role priority, then by name
  roots.sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`));
  // Sort children too
  const sortChildren = (node) => {
    node.children.sort((a, b) => (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`));
    node.children.forEach(sortChildren);
  };
  roots.forEach(sortChildren);
  return roots;
};

// ── Mobile list node ──────────────────────────────────────────────────────────

const OrgNodeList = ({ node, onOpen, canClick }) => {
  const rc = ROLE_CONFIG[node.role] || ROLE_CONFIG.collaborateur;
  const hasChildren = node.children?.length > 0;

  return (
    <div>
      <div
        onClick={() => canClick && onOpen?.(node.id)}
        className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-200 ${
          canClick ? "cursor-pointer hover:bg-[#F0FAF4] dark:hover:bg-[#112240]" : ""
        }`}
      >
        <Avatar className="w-10 h-10 shrink-0 ring-2 ring-white dark:ring-[#0B1628] shadow-sm">
          <AvatarImage src={node.avatar_url} className="object-cover" />
          <AvatarFallback className="text-sm font-semibold bg-gradient-to-br from-[#1A4D2E] to-[#2D7A4E] text-white">
            {getInitials(node.first_name, node.last_name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-[#111827] dark:text-[#D6E4F5] truncate">
            {node.first_name} {node.last_name}
          </p>
          {node.position && (
            <p className="text-xs text-[#6B7280] dark:text-[#7EA8CE] truncate">{node.position}</p>
          )}
        </div>
        <Badge className={`shrink-0 text-xs border-0 hidden sm:inline-flex ${rc.className}`}>
          {rc.label}
        </Badge>
      </div>

      {hasChildren && (
        <div className="ml-8 border-l-2 border-gray-200 dark:border-[#1A3050]">
          {node.children.map((child) => (
            <div key={child.id} className="relative pl-5">
              <div className="absolute left-0 top-7 w-5 h-0.5 bg-gray-200 dark:bg-[#1A3050]" />
              <OrgNodeList node={child} onOpen={onOpen} canClick={canClick} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Desktop hierarchy: node card ──────────────────────────────────────────────

const HierarchyCard = ({ node, onOpen, canClick }) => {
  const rc = ROLE_CONFIG[node.role] || ROLE_CONFIG.collaborateur;
  return (
    <div
      onClick={() => canClick && onOpen?.(node.id)}
      className={`flex flex-col items-center text-center p-3 rounded-2xl border dark:border-[#1A3050] bg-white dark:bg-[#0B1628] shadow-sm transition-all duration-200 w-[120px] shrink-0 ${
        canClick
          ? "cursor-pointer hover:-translate-y-1 hover:shadow-md hover:border-[#1A4D2E]/40 dark:hover:border-[#4CAF70]/30 hover:bg-[#F0FAF4] dark:hover:bg-[#112240]"
          : ""
      }`}
    >
      <Avatar className="w-12 h-12 mb-2 ring-2 ring-white dark:ring-[#0B1628] shadow-sm">
        <AvatarImage src={node.avatar_url} className="object-cover" />
        <AvatarFallback className="text-sm font-semibold bg-gradient-to-br from-[#1A4D2E] to-[#2D7A4E] text-white">
          {getInitials(node.first_name, node.last_name)}
        </AvatarFallback>
      </Avatar>
      <p className="font-semibold text-[11px] text-[#111827] dark:text-[#D6E4F5] leading-tight w-full truncate">
        {node.first_name} {node.last_name}
      </p>
      {node.position && (
        <p className="text-[10px] text-[#6B7280] dark:text-[#7EA8CE] mt-0.5 w-full truncate">
          {node.position}
        </p>
      )}
      <Badge className={`mt-1.5 text-[9px] px-1.5 py-0 border-0 ${rc.className}`}>
        {rc.label}
      </Badge>
    </div>
  );
};

// ── Desktop hierarchy: recursive tree node (li element) ──────────────────────

const HierarchyNode = ({ node, onOpen, canClick }) => (
  <li className="org-tree-node">
    <HierarchyCard node={node} onOpen={onOpen} canClick={canClick} />
    {node.children.length > 0 && (
      <ul className="org-tree-children">
        {node.children.map((child) => (
          <HierarchyNode key={child.id} node={child} onOpen={onOpen} canClick={canClick} />
        ))}
      </ul>
    )}
  </li>
);

// ── Desktop hierarchy chart ───────────────────────────────────────────────────

const OrgChartHierarchy = ({ roots, onOpen, canClick }) => (
  <>
    <style>{`
      .org-tree-root {
        display: flex;
        justify-content: center;
        gap: 32px;
        list-style: none;
        padding: 0;
        margin: 0;
      }
      .org-tree-root > .org-tree-node {
        padding-top: 0;
      }
      .org-tree-root > .org-tree-node::before,
      .org-tree-root > .org-tree-node::after {
        display: none;
      }
      .org-tree-children {
        display: flex;
        justify-content: center;
        gap: 16px;
        list-style: none;
        padding: 0;
        padding-top: 32px;
        margin: 0;
        position: relative;
      }
      /* Vertical line from parent card down to horizontal bar */
      .org-tree-children::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 2px;
        height: 32px;
        background-color: #e5e7eb;
      }
      .dark .org-tree-children::before {
        background-color: #1A3050;
      }
      /* Each child node */
      .org-tree-node {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        padding-top: 32px;
      }
      /* Horizontal bar — left half */
      .org-tree-node::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 50%;
        height: 2px;
        background-color: #e5e7eb;
      }
      .dark .org-tree-node::before {
        background-color: #1A3050;
      }
      /* Horizontal bar — right half */
      .org-tree-node::after {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        right: 0;
        height: 2px;
        background-color: #e5e7eb;
      }
      .dark .org-tree-node::after {
        background-color: #1A3050;
      }
      /* First child: horizontal bar starts at center (no left overhang) */
      .org-tree-node:first-child::before { left: 50%; }
      /* Last child: horizontal bar ends at center (no right overhang) */
      .org-tree-node:last-child::after  { right: 50%; }
      /* Only child: no horizontal bar, just the vertical line from parent */
      .org-tree-node:only-child::before,
      .org-tree-node:only-child::after  { display: none; }
    `}</style>
    <div className="overflow-x-auto pb-4">
      <div className="min-w-max px-4 py-2">
        <ul className="org-tree-root">
          {roots.map((root) => (
            <HierarchyNode key={root.id} node={root} onOpen={onOpen} canClick={canClick} />
          ))}
        </ul>
      </div>
    </div>
  </>
);

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * OrgChart
 * mode="list"      → indented vertical tree (mobile default)
 * mode="hierarchy" → visual org chart with connecting lines (desktop)
 */
const OrgChart = ({ users, onOpen, canClick = false, mode = "list" }) => {
  const roots = useMemo(() => buildOrgTree(users), [users]);

  if (roots.length === 0) {
    return (
      <div className="text-center py-16 text-[#9CA3AF] dark:text-[#3D607E]">
        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Aucun collaborateur trouvé</p>
      </div>
    );
  }

  if (mode === "hierarchy") {
    return <OrgChartHierarchy roots={roots} onOpen={onOpen} canClick={canClick} />;
  }

  // Default: list mode
  return (
    <div className="space-y-1 p-2">
      {roots.map((root) => (
        <OrgNodeList key={root.id} node={root} onOpen={onOpen} canClick={canClick} />
      ))}
    </div>
  );
};

export default OrgChart;
